import React from 'react';
import { Scale, FileText, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { SEO } from './SEO';

export const TermsConditions = () => {
  return (
    <div style={{
      maxWidth: '1000px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '80px'
    }}>
      <SEO 
        title="Terms & Conditions" 
        description="Toolmetrico Terms and Conditions. Read the legal agreements, warranties, and user representations required to use our suite of free digital tools."
        keywords="terms and conditions, toolmetrico terms, legal agreement, website terms of service, user guidelines"
      />
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '32px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Terms & Conditions</span>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(5, 117, 251, 0.1)',
          color: 'var(--primary)',
          width: '64px',
          height: '64px',
          borderRadius: '20px',
          marginBottom: '20px'
        }}>
          <Scale size={32} />
        </div>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 16px 0', letterSpacing: '-0.02em' }}>
          Terms & Conditions
        </h1>
        <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 }}>
          Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      <div className="legal-content-container">
        <section style={{ marginBottom: '40px' }}>
          <h2 className="legal-heading">
            <FileText size={20} style={{ color: 'var(--primary)' }} /> 1. Agreement to Terms
          </h2>
          <p>
            These Terms and Conditions constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and Toolmetrico ("we," "us," or "our"), concerning your access to and use of our website and related tools.
          </p>
          <p>
            By accessing our tools, you agree that you have read, understood, and agreed to be bound by all of these Terms and Conditions. If you do not agree with all of these terms, you are expressly prohibited from using the site and must discontinue use immediately.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 className="legal-heading">
            <CheckCircle size={20} style={{ color: 'var(--primary)' }} /> 2. Intellectual Property Rights
          </h2>
          <p>
            Unless otherwise indicated, the Site is our proprietary property and all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics on the Site (collectively, the "Content") and the trademarks, service marks, and logos contained therein are owned or controlled by us or licensed to us.
          </p>
          <p>
            You are granted a limited license to access and use the tools. You may freely use the outputs generated by our tools (such as QR codes, barcodes, compressed images, and minified code) for your own commercial or personal projects without attribution.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 className="legal-heading">3. User Representations</h2>
          <p>
            By using the tools, you represent and warrant that:
          </p>
          <ul style={{
            fontSize: '0.95rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.8,
            listStyleType: 'none',
            padding: 0,
            margin: '16px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            textAlign: 'left'
          }}>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>You will not use the tools for any illegal or unauthorized purpose;</div>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>Your use of the tools will not violate any applicable law or regulation;</div>
            </li>
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ color: 'var(--primary)', fontWeight: 'bold', fontSize: '1.2rem', lineHeight: '1.4rem', userSelect: 'none' }}>•</span>
              <div>You will not attempt to bypass our security measures or exploit vulnerabilities in the client-side processing architecture.</div>
            </li>
          </ul>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 className="legal-heading">4. Disclaimer of Warranties</h2>
          <p>
            THE SITE IS PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF THE SITE AND OUR SERVICES WILL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SITE AND YOUR USE THEREOF.
          </p>
          <p>
            We make no warranties or representations about the accuracy or completeness of the site's content and assume no liability or responsibility for any (1) errors, mistakes, or inaccuracies of content and materials, or (2) any unauthorized access to or use of our secure servers and/or any and all personal information stored therein.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 className="legal-heading">5. Limitations of Liability</h2>
          <p>
            IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES ARISING FROM YOUR USE OF THE SITE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
        </section>

        <section style={{ marginBottom: '40px' }}>
          <h2 className="legal-heading">6. Modifications and Interruptions</h2>
          <p>
            We reserve the right to change, modify, or remove the contents of the Site at any time or for any reason at our sole discretion without notice. We also reserve the right to modify or discontinue all or part of the tools without notice at any time. We will not be liable to you or any third party for any modification, price change, suspension, or discontinuance of the Site.
          </p>
        </section>

        <section>
          <h2 className="legal-heading">7. Governing Law</h2>
          <p>
            These Terms shall be governed by and defined following the laws of the jurisdiction in which Toolmetrico is headquartered. Toolmetrico and yourself irrevocably consent that the courts of that jurisdiction shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these terms.
          </p>
        </section>
      </div>

      <style>{`
        .breadcrumb-back:hover {
          color: var(--primary) !important;
          transform: translateX(-2px);
        }
        
        .legal-content-container {
          background: #ffffff;
          border: 1px solid var(--border-color);
          border-radius: 24px;
          padding: 40px;
          box-shadow: var(--shadow-sm);
          color: var(--text-primary);
          line-height: 1.8;
          font-size: 1rem;
        }

        .legal-heading {
          font-size: 1.5rem;
          font-weight: 800;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        @media (max-width: 768px) {
          .legal-content-container {
            padding: 24px 20px;
            border-radius: 16px;
          }
          .legal-heading {
            font-size: 1.25rem;
          }
        }
      `}</style>
    </div>
  );
};
